import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import { styles } from "./styles";
import { FileStateReverseMap, SingleProjectProps } from "./types";
import { Collaborators } from "../TracksPageComponents/Collaborators";
import { useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import {
  AuthContext,
  AuthContextInterface,
  TalkJsContextInterface,
} from "../../contexts";
import { TalkJsContext, UserDetailsContext } from "../../contexts";
import { handleCreateGroupChat } from "../TrackComponents/TrackHandler/trackHandlers";
import {
  Collaborator,
  DownloadProjectOrFolderResponse,
  FileState,
  ProjectFile,
  Role,
} from "../../proto/api_pb";
import { Project } from "../../proto/api_pb";
import { UpdateProjectResponse } from "../../proto/api_pb";
import { toast } from "react-toastify";
import { downloadProject } from "../../services/projectService";
import { updateProject } from "../../services/projectService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import JSZip from "jszip";
import { EditProjectModal } from "../Modal/EditProjectModal";
import DeleteConfirmationModal from "../Modal/DeleteConfirmationModal";
import { APP_COLOR } from "../../theme";
import { ShareProjectModal } from "../Modal/ShareProjectModal";
export const SingleProject: React.FC<SingleProjectProps> = ({
  project,
  session,
  setInbox,
  handleUseEffect,
  openFoldersList,
  setOpenFoldersList,
  removeDeletedProject,
}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { getValidAccessToken } = useGetValidAccessToken();
  const talkJsContext = useContext<TalkJsContextInterface>(TalkJsContext);
  const { enterprise } = useContext(UserDetailsContext);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);

  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();

  const createGroupChat = (
    id: string | undefined,
    title: string | undefined,
    contributors: Collaborator[] | undefined
  ) => {
    handleCreateGroupChat(
      talkJsContext,
      contributors,
      navigate,
      undefined,
      id,
      title
    );
  };

  const downloadEntireProject = async () => {
    try {
      setLoading(true);
      const token = await getValidAccessToken();
      const res = await downloadProject(
        token,
        enterprise?.id as string,
        userId,
        project.getId()!,
      );

      if (res instanceof DownloadProjectOrFolderResponse) {
        const projectFiles = res.getProjectFilesList();
        const trackFiles = res.getTracksList();

        if (!projectFiles.length && !trackFiles.length) {
          toast.warn("No files or tracks found in the project");
          setLoading(false);
          return;
        }

        const zip = new JSZip();

        const projectFilePromises = projectFiles.map(
          async (file: ProjectFile) => {
            const fileUrl = file.getFileUrl();
            const fileName = file.getFileName();
            if (fileUrl) {
              try {
                const response = await fetch(fileUrl);
                if (!response.ok)
                  throw new Error(`Failed to fetch file: ${fileName}`);
                const fileBlob = await response.blob();
                zip.file(`Files/${fileName}`, fileBlob);
              } catch (error) {
                console.error(`Error downloading file ${fileName}:`, error);
              }
            }
          }
        );

        const trackFilePromises = trackFiles.map(async (trackResponse) => {
          const trackVersion = trackResponse.getLatestTrackVersion();
          const track = trackResponse.getTrack();

          if (!trackVersion) {
            console.error("Track version is undefined for track:");
            return;
          }

          const trackUrl = trackVersion.getTrackUrl();

          let fileExtension = ".mp3";
          if (trackUrl) {
            const urlParts = trackUrl.split(".");
            if (urlParts.length > 1) {
              fileExtension = `.${urlParts[urlParts.length - 1].split("?")[0]}`;
            }
          }

          const trackName = `${(track?.getTitle() || "Unknown").substring(
            0,
            30
          )}${fileExtension}`;

          if (trackUrl) {
            try {
              const response = await fetch(trackUrl);
              if (!response.ok)
                throw new Error(`Failed to fetch track: ${trackName}`);
              const trackBlob = await response.blob();

              if (response.headers.has("content-type")) {
                const contentType = response.headers.get("content-type");
                if (contentType) {
                  const mimeToExt = {
                    "audio/mpeg": ".mp3",
                    "audio/wav": ".wav",
                    "audio/x-wav": ".wav",
                    "audio/ogg": ".ogg",
                    "audio/flac": ".flac",
                    "audio/aac": ".aac",
                    "audio/mp4": ".m4a",
                    // Add more MIME types as needed
                  };

                  for (const [mime, ext] of Object.entries(mimeToExt)) {
                    if (contentType.includes(mime)) {
                      fileExtension = ext;
                      break;
                    }
                  }
                }
              }

              const finalTrackName = `${(
                track?.getTitle() || "Unknown"
              ).substring(0, 50)}${fileExtension}`;
              zip.file(`Tracks/${finalTrackName}`, trackBlob);
            } catch (error) {
              console.error(`Error downloading track ${trackName}:`, error);
            }
          } else {
            console.error(`No URL found for track: ${trackName}`);
          }
        });

        await Promise.all([...projectFilePromises, ...trackFilePromises]);

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${project.getName()}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Project is Ready to download!");
      } else {
        console.error("Failed to download project", res);
      }
    } catch (error) {
      console.error("Error downloading project:", error);
      toast.error("Failed to download project.");
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async () => {
    const p = new Project();
    p.setId(project.getId());
    p.setOwnerId(project.getOwnerId());
    p.setPrivate(project.getPrivate());
    p.setName(project.getName());
    p.setDescription(project.getDescription());
    p.setArtworkUrl(project.getArtworkUrl());
    p.setProjectState(FileStateReverseMap[FileState.FILE_STATE_DELETED]);
    p.setModifiedTimestamp(Date.now());
    p.setCollaboratorsList(project.getCollaboratorsList());
    p.setTimestamp(project.getTimestamp());
    p.setEnterpriseId(enterprise?.id as string);
    const token = await getValidAccessToken();
    const res = await updateProject(token, p);
    if (res instanceof UpdateProjectResponse) {
      setIsConfirmationModalOpen(false);
      toast.info("Project deleted");
    }
    removeDeletedProject(project.getId()!);
  };

  return (
    <>
      {isConfirmationModalOpen && (
        <DeleteConfirmationModal
          isMessageModalOpen={isConfirmationModalOpen}
          message="Are you sure you want to delete the project?"
          toggle={() => setIsConfirmationModalOpen((prev) => !prev)}
          handleConfirm={deleteProject}
        />
      )}
      <Box
        sx={styles.singleProjectMainContainer}
        onClick={() => {
          if (setOpenFoldersList) setOpenFoldersList([]);
          navigate(
            `/project/${encodeURIComponent(
              project.getName()
            )}/${project.getId()}`
          );
        }}
      >
        <Box sx={{ position: "relative" }}>
          <img
            style={styles.img}
            src={
              project.getArtworkUrl()?.includes("http")
                ? project.getArtworkUrl()
                : enterprise?.image
            }
            alt={`${project.getId()}`}
          />
          <Box sx={{ position: "absolute", bottom: 0, right: 0 }}>
            <Collaborators
              collaborators={project.getCollaboratorsList()}
              isGridView={true}
            />
          </Box>
        </Box>
        <Typography sx={{ marginTop: "5px" }}>{project.getName()}</Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              downloadEntireProject();
            }}
          >
            {loading ? (
              <CircularProgress sx={{ color: APP_COLOR }} size={25} />
            ) : (
              <DownloadIcon sx={{ fontSize: "20px" }} />
            )}
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              const contributors = project.getCollaboratorsList();
              if (session) {
                const talkInbox = session.createInbox();
                setInbox(talkInbox);
                talkJsContext.setIsChatOpen(true);
                createGroupChat(
                  project.getId(),
                  project.getName(),
                  contributors
                );
              }
              if (isSmallScreen) navigate("/messages");
            }}
          >
            <img
              style={{
                filter: "contrast(100)",
                width: "20px",
              }}
              src="/assets/icons/TracksPageIcons/message-chat-circle.svg"
              alt="icon"
            />
          </IconButton>
          <ShareProjectModal id={project.getId()} title={project.getName()} />
          {/* <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toast.warn("Coming Soon");
            }}
          >
            <LinkIcon sx={{ fontSize: "20px" }} />
          </IconButton> */}
          {Number(enterprise?.role) !== Role.ROLE_ARTIST && (
            <EditProjectModal
              project={project}
              handleUseEffect={handleUseEffect}
            />
          )}
          {Number(enterprise?.role) !== Role.ROLE_ARTIST && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsConfirmationModalOpen(true);
              }}
            >
              <DeleteIcon sx={{ fontSize: "20px" }} />
            </IconButton>
          )}
        </Box>
      </Box>
    </>
  );
};
